import { useState } from 'react'
import * as Yup from 'yup'
import clsx from 'clsx'
import { Link } from 'react-router-dom'
import { useFormik } from 'formik'
import { Loader2, AlertCircle, Mail, ArrowLeft, Send, CheckCircle2 } from 'lucide-react'
import { requestForgotPassword } from '../core/_requests'
import axios from 'axios'

interface ForgotPasswordFormValues {
  email: string
}

export function ForgotPassword() {
  const [loading, setLoading] = useState(false)
  const [sent, setSent] = useState(false)
  const [errorMsg, setErrorMsg] = useState('')

  const forgotPasswordSchema = Yup.object().shape({
    email: Yup.string()
      .email('Wrong email format')
      .min(3, 'Minimum 3 symbols')
      .max(50, 'Maximum 50 symbols')
      .required('Email is required'),
  })

  const formik = useFormik<ForgotPasswordFormValues>({
    initialValues: { email: '' },
    validationSchema: forgotPasswordSchema,
    onSubmit: async (values, { setStatus }) => {
      setLoading(true)
      setErrorMsg('')
      try {
        await requestForgotPassword(values.email.trim())
        setSent(true)
      } catch (error: any) {
        let message = 'Something went wrong. Please try again later.'
        if (axios.isAxiosError(error)) {
          if (error.response?.data?.message) {
            message = error.response.data.message
          } else if (error.response?.status === 503 || (!error.response && error.request)) {
            message = 'Our service is temporarily unavailable. Please try again in a few minutes.'
          }
        }
        setErrorMsg(message)
        setStatus(message)
      } finally {
        setLoading(false)
      }
    },
  })

  if (sent) {
    return (
      <div>
        <Link to="/auth/login" className="inline-flex items-center gap-1.5 text-xs font-medium text-slate-500 hover:text-slate-800 mb-5">
          <ArrowLeft className="w-3.5 h-3.5" />
          Back to sign in
        </Link>

        <div className="flex flex-col items-center text-center pt-6">
          <div className="w-14 h-14 rounded-full bg-green-100 flex items-center justify-center mb-5">
            <CheckCircle2 className="w-7 h-7 text-green-600" />
          </div>
          <h2 className="text-2xl font-bold text-slate-900">Check your email</h2>
          <p className="mt-2 text-sm text-slate-500 max-w-xs">
            If an account exists for <strong>{formik.values.email}</strong>, we've sent a password reset link.
            Please check your inbox and spam folder.
          </p>
          <p className="mt-4 text-xs text-slate-400">
            The link expires in 1 hour.
          </p>
        </div>

        <p className="mt-10 text-center text-sm text-slate-600">
          Didn't receive it?{' '}
          <button
            type="button"
            onClick={() => { setSent(false); formik.submitForm() }}
            className="text-blue-600 font-semibold hover:underline"
          >
            Send again
          </button>
        </p>
      </div>
    )
  }

  return (
    <form
      onSubmit={formik.handleSubmit}
      noValidate
      id='kt_login_signin_form'
    >
      <Link to="/auth/login" className="inline-flex items-center gap-1.5 text-xs font-medium text-slate-500 hover:text-slate-800 mb-5">
        <ArrowLeft className="w-3.5 h-3.5" />
        Back to sign in
      </Link>

      <h2 className="text-2xl font-bold text-slate-900">Forgot your password?</h2>
      <p className="mt-1 text-sm text-slate-500">Enter your work email and we will send you a reset link.</p>

      <div className="mt-7 space-y-4">
        {/* Error display */}
        {errorMsg && (
          <div
            role="alert"
            className="flex items-start gap-2 px-3 py-2.5 rounded-lg border border-red-200 bg-red-50 text-xs text-red-700"
          >
            <AlertCircle className="w-4 h-4 mt-0.5 flex-shrink-0" />
            <span>{errorMsg}</span>
          </div>
        )}

        {/* Email */}
        <div>
          <label htmlFor="email" className="block text-xs font-medium text-slate-700 mb-1.5">
            Work email
          </label>
          <div className="relative">
            <Mail className="w-4 h-4 text-slate-400 absolute left-3 top-1/2 -translate-y-1/2 pointer-events-none" />
            <input
              id="email"
              type="email"
              required
              autoComplete="email"
              placeholder="you@company.com"
              className={clsx(
                'w-full pl-9 pr-3 py-2.5 text-sm rounded-lg border bg-white shadow-sm placeholder:text-slate-400 focus:outline-none focus:border-blue-500 focus:ring-2 focus:ring-blue-100',
                formik.touched.email && formik.errors.email
                  ? 'border-red-300'
                  : 'border-slate-200'
              )}
              {...formik.getFieldProps('email')}
            />
          </div>
          {formik.touched.email && formik.errors.email && (
            <p className="mt-1 text-xs text-red-600">{formik.errors.email}</p>
          )}
        </div>

        {/* Submit */}
        <button
          type="submit"
          disabled={loading}
          className="w-full inline-flex items-center justify-center gap-2 px-4 py-2.5 rounded-lg bg-blue-600 text-white text-sm font-semibold shadow-sm hover:bg-blue-700 transition-colors disabled:opacity-60 disabled:cursor-not-allowed"
        >
          {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />}
          {loading ? 'Sending…' : 'Send Reset Link'}
        </button>
      </div>

      <p className="mt-7 text-center text-sm text-slate-600">
        Remember your password?{' '}
        <Link to="/auth/login" className="text-blue-600 font-semibold hover:underline">
          Sign in
        </Link>
      </p>
    </form>
  )
}
